import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiHome,
  FiCalendar,
  FiSearch,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { BarLoader } from "react-spinners";

const BeneficiaryTrace = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGender, setFilterGender] = useState("all");
  const [filterAge, setFilterAge] = useState("all");
  const [userCSO, setUserCSO] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const fetchUserAndBeneficiaries = async () => {
      try {
        setLoading(true);

        // Get the current user's info
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            withCredentials: true,
          }
        );

        const userData = userResponse.data;
        const userRegisteredTo = userData.userId;

        setUserCSO(userRegisteredTo);

        // Fetch beneficiaries assigned to this CSO
        const beneficiariesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/beneficiaries_trace?registeredTo=${userRegisteredTo}`,
          { withCredentials: true }
        );

        setBeneficiaries(beneficiariesResponse.data.beneficiaries || []);
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndBeneficiaries();
  }, []);

  // Filter beneficiaries based on search and filter criteria
  const filteredBeneficiaries = beneficiaries.filter((beneficiary) => {
    const matchesSearch =
      beneficiary.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beneficiary.phone?.includes(searchTerm) ||
      beneficiary.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beneficiary.beneficiary_id?.includes(searchTerm);

    const matchesGender =
      filterGender === "all" || beneficiary.gender === filterGender;

    const matchesAge =
      filterAge === "all" ||
      (filterAge === "child" && parseInt(beneficiary.age) < 18) ||
      (filterAge === "adult" && parseInt(beneficiary.age) >= 18);

    return matchesSearch && matchesGender && matchesAge;
  });

  // Get current beneficiaries for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBeneficiaries = filteredBeneficiaries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBeneficiaries.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  const downloadDocument = (fileUrl, fileName) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_API_URL}/${fileUrl}`;
    link.target = "_blank";
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Only show limited page numbers with ellipsis
  const getDisplayedPageNumbers = () => {
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) return pageNumbers;

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(currentPage - half, 1);
    let end = Math.min(start + maxVisiblePages - 1, totalPages);

    if (end - start < maxVisiblePages - 1) {
      start = Math.max(end - maxVisiblePages + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <BarLoader color="#4F46E5" height={2} width={200} />
          <p className="mt-4 text-gray-600">Loading beneficiaries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-lg shadow-md text-center max-w-md">
          <FiUser className="mx-auto text-red-500 text-4xl mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Beneficiary Trace
              </h1>
              <p className="text-gray-600">
                Track the status of beneficiaries assigned to your CSO:
                <span className="font-semibold text-blue-600 ml-1"></span>
              </p>
            </div>
            <div className="bg-blue-50 px-4 py-2 rounded-lg">
              <p className="text-blue-700 font-medium">
                Total: {beneficiaries.length} beneficiary
                {beneficiaries.length !== 1 ? "ies" : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Input */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, email, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Gender Filter */}
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Age Filter */}
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterAge}
                onChange={(e) => setFilterAge(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Ages</option>
                <option value="child">Children (&lt;18)</option>
                <option value="adult">Adults (18+)</option>
              </select>
            </div>
          </div>
        </div>
        {beneficiaries.length > 0 && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl shadow-md text-center">
              <div className="text-2xl font-bold text-blue-600">
                {beneficiaries.length}
              </div>
              <div className="text-sm text-gray-600">Total Beneficiaries</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md text-center">
              <div className="text-2xl font-bold text-green-600">
                {beneficiaries.filter((b) => b.gender === "male").length}
              </div>
              <div className="text-sm text-gray-600">Male</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md text-center">
              <div className="text-2xl font-bold text-pink-600">
                {beneficiaries.filter((b) => b.gender === "female").length}
              </div>
              <div className="text-sm text-gray-600">Female</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md text-center">
              <div className="text-2xl font-bold text-purple-600">
                {beneficiaries.filter((b) => parseInt(b.age) < 18).length}
              </div>
              <div className="text-sm text-gray-600">Children (&lt;18)</div>
            </div>
          </div>
        )}
        {/* Beneficiaries List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {filteredBeneficiaries.length === 0 ? (
            <div className="p-8 text-center">
              <FiUser className="mx-auto text-gray-400 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-600">
                No beneficiaries found
              </h3>
              <p className="text-gray-500">
                {beneficiaries.length === 0
                  ? "No beneficiaries have been assigned to your CSO yet."
                  : "No beneficiaries match your search criteria."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name & Details
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentBeneficiaries.map((beneficiary) => (
                      <tr
                        key={beneficiary.id}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {beneficiary.beneficiary_id}
                          </div>
                          <div className="text-xs text-gray-500">
                            Added:{" "}
                            {new Date(beneficiary.createdAt).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Updated:{" "}
                            {new Date(beneficiary.updatedAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              <img
                                className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-sm"
                                src={`${process.env.REACT_APP_API_URL}/${beneficiary.photo}`}
                                alt={beneficiary.fullName}
                                onError={(e) => {
                                  e.target.src =
                                    "https://ui-avatars.com/api/?name=" +
                                    encodeURIComponent(beneficiary.fullName) +
                                    "&background=random";
                                }}
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {beneficiary.fullName}
                              </div>
                              <div className="text-sm text-gray-500 capitalize">
                                {beneficiary.gender}, {beneficiary.age} years
                                {beneficiary.school && `, ${beneficiary.school}, `}
                                <span>Birth Certificate ID: {beneficiary.birth_certificate_id && ` ${beneficiary.birth_certificate_id}`}</span>

                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <FiPhone className="mr-2 text-gray-400" />
                            {beneficiary.phone}
                          </div>
                          {beneficiary.email && (
                            <div className="text-sm text-gray-500 flex items-center mt-1">
                              <FiMail className="mr-2 text-gray-400" />
                              {beneficiary.email}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <FiMapPin className="mr-2 text-gray-400 flex-shrink-0" />
                              <span>Kebele: {beneficiary.kebele}</span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1 flex">
                              <FiHome className="mr-2 text-gray-400 flex-shrink-0" />
                              <span>House: {beneficiary.houseNo}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {beneficiary.wereda}, {beneficiary.kfleketema}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            {beneficiary.idFile && (
                              <button
                                onClick={() =>
                                  downloadDocument(
                                    beneficiary.idFile,
                                    `${beneficiary.fullName}-ID`
                                  )
                                }
                                className="flex items-center text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                              >
                                <FiDownload className="mr-1" />
                                ID
                              </button>
                            )}
                            {beneficiary.birth_certificate && (
                              <button
                                onClick={() =>
                                  downloadDocument(
                                    beneficiary.birth_certificate,
                                    `${beneficiary.fullName}-Birth-Certificate`
                                  )
                                }
                                className="flex items-center text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200"
                              >
                                <FiDownload className="mr-1" />
                                Birth Cert
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, filteredBeneficiaries.length)}
                    </span>{" "}
                    of <span className="font-medium">{filteredBeneficiaries.length}</span> results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={goToFirstPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <FiChevronsLeft size={18} />
                    </button>
                    <button
                      onClick={goToPrevPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <FiChevronLeft size={18} />
                    </button>

                    {getDisplayedPageNumbers().map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-1 rounded-md ${currentPage === number ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        {number}
                      </button>
                    ))}

                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <FiChevronRight size={18} />
                    </button>
                    <button
                      onClick={goToLastPage}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      <FiChevronsRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryTrace;